import { Component, ChangeDetectionStrategy, ChangeDetectorRef, OnInit, DestroyRef, inject, ViewChild, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { debounceTime } from 'rxjs/operators';
import { Subscription } from 'rxjs';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';

import { ScreeningDetailPage } from 'src/pages/leasing/screening-detail/screening-detail';

import { ParseDateTimePipe } from 'src/shared/pipes/parse-date.pipe';

import { FeatureGateService } from 'src/shared/services/feature-gate.service';
import { TenantScreeningService, TenantScreening } from 'src/shared/services/tenant-screening.service';
import { RentalUnitsService, RentalUnit } from 'src/shared/services/rental-units.service';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

// View-model: a screening plus a precomputed "Tenant · Room A" style label for
// room tenants on by-room units (undefined for everyone else — unchanged rendering).
interface ScreeningRow extends TenantScreening {
  memberLabel?: string;
}

@Component({
  selector: 'screenings-tab',
  templateUrl: './screenings-tab.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IonicModule,
    TidyUiComponentsModule,
    SharedModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ScreeningsTabComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private fb = inject(FormBuilder);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private featureGate = inject(FeatureGateService);
  private screeningService = inject(TenantScreeningService);
  private rentalUnitsService = inject(RentalUnitsService);

  readonly windowService = inject(WindowService);

  searchForm: UntypedFormGroup;
  screenings: ScreeningRow[] = [];
  headers: string[] = ['Name', 'Property', 'Requested', 'Credit', 'Recommendation', 'Status', 'last-column'];
  allRows: any[] = [];
  shownRows: any[] = [];
  // Mobile cards render raw ScreeningRow objects, not table-row VMs, so the
  // search filter maintains this parallel collection (same ids as shownRows).
  shownScreenings: ScreeningRow[] = [];
  loaded = false;
  errorMessage = '';
  private panelClosedSubscription: Subscription;

  orderAction = () => this.orderScreening();

  @ViewChild('tidyTable') tidyTable: any;

  constructor() {
    this.searchForm = this.fb.group({ search: [''] });
    this.searchForm.get('search')?.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => this.updateSearch(value));
  }

  async ngOnInit() {
    try {
      await this.loadPage();
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to load screenings';
      this.loaded = true;
      this.cdr.markForCheck();
    }
  }

  // Email -> room name, for room tenants on by-room units. Built once on load
  // so templates never call a function; screenings for non-room tenants (the
  // vast majority) simply have no entry and render exactly as before.
  private roomNameByEmail = new Map<string, string>();

  async loadPage() {
    const [screenings, units] = await Promise.all([
      this.screeningService.getScreenings(),
      this.rentalUnitsService.getUnits(),
    ]);
    this.screenings = screenings;
    this.roomNameByEmail = this.buildRoomNameMap(units);
    this.allRows = this.buildRows(this.screenings);
    this.shownRows = [...this.allRows];
    this.shownScreenings = [...this.screenings];
    this.loaded = true;
    this.cdr.markForCheck();
  }

  private buildRoomNameMap(units: RentalUnit[]): Map<string, string> {
    const map = new Map<string, string>();
    for (const unit of units) {
      if (!this.rentalUnitsService.isByRoom(unit)) { continue; }
      for (const space of unit.spaces || []) {
        if (space.tenant_email) { map.set(space.tenant_email.toLowerCase(), space.name); }
      }
    }
    return map;
  }

  private memberLabel(s: TenantScreening): string {
    const roomName = this.roomNameByEmail.get(s.email?.toLowerCase());
    return roomName ? `${s.applicant_type} · ${roomName}` : s.applicant_type;
  }

  buildRows(screenings: ScreeningRow[]): any[] {
    // Mobile cards render the raw ScreeningRow objects (via shownScreenings),
    // so the member label is precomputed onto each one here (no function
    // calls in the template).
    screenings.forEach(s => { s.memberLabel = this.memberLabel(s); });
    // `requested_at` is date-only — `new Date()` would read it as UTC midnight
    // and render the previous day west of UTC.
    const datePipe = new ParseDateTimePipe();
    return screenings.map(s => ({
      id: s.id,
      'Name': { value: s.applicant_name, initials: true },
      'Property': { value: `${s.property_name  }  ·  ${  s.memberLabel}` },
      'Requested': { value: datePipe.transform(s.requested_at, 'MMM d, yyyy') },
      'Credit': { value: s.credit_score !== null ? String(s.credit_score) : '—' },
      'Recommendation': { value: this.screeningService.recommendationLabel(s.recommendation) },
      'Status': { value: s.status },
      'last-column': { value: '', hasChevron: true },
    }));
  }

  recommendationColor(rec: any): string { return this.screeningService.recommendationColor(rec); }
  recommendationLabel(rec: any): string { return this.screeningService.recommendationLabel(rec); }
  statusColor(status: any): string { return this.screeningService.statusColor(status); }

  updateSearch(search: string) {
    if (!search || search.length < 2) {
      this.shownRows = [...this.allRows];
      this.shownScreenings = [...this.screenings];
      this.cdr.markForCheck();
      return;
    }
    const term = search.toLowerCase();
    this.shownRows = this.allRows.filter(row =>
      row['Name'].value.toLowerCase().includes(term) ||
      row['Property'].value.toLowerCase().includes(term)
    );
    const shownIds = new Set(this.shownRows.map(row => row.id));
    this.shownScreenings = this.screenings.filter(s => shownIds.has(s.id));
    this.cdr.markForCheck();
  }

  onRowClick(event: { column: string; element: any }): void {
    const screening = this.screenings.find(s => s.id === event.element.id);
    if (screening) { this.goToPage('screening-detail', { screening }, ScreeningDetailPage); }
  }

  orderScreening(): void {
    this.featureGate.gate();
  }

  async goToPage(url: string, params: any, component: any): Promise<void> {
    const isMobileResolution = !this.windowService.isDesktopRes;
    if (!isMobileResolution) {
      const isRightSidePanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isRightSidePanelAlreadyOpen) {
        this.rightSidePanelService.navigateTo(url, params, component);
      } else {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(component);
      }
      this.panelClosedSubscription?.unsubscribe();
      this.panelClosedSubscription = this.rightSidePanelService.panelClosed().pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(async () => { await this.loadPage(); });
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }
}
